// Thin wrapper over the Web Speech API (SpeechSynthesis) for voice-guided
// games. Degrades gracefully: callers should also offer an on-screen fallback.

export const isSpeechSupported = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  typeof window.SpeechSynthesisUtterance !== "undefined";

const getVoices = () => {
  try {
    return window.speechSynthesis.getVoices() || [];
  } catch (e) {
    return [];
  }
};

// Voices load asynchronously in Chrome; warm them up so the first speak() has
// a voice ready.
export const primeVoices = () => {
  if (!isSpeechSupported()) return;
  try {
    getVoices();
  } catch (e) {
    // ignore
  }
};

const pickEnglishVoice = () => {
  const voices = getVoices();
  return (
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("en")) ||
    voices[0] ||
    null
  );
};

const dispatch = (text) => {
  const synth = window.speechSynthesis;
  // Only cancel if something is actually queued. Calling cancel() on an idle
  // queue right before speak() can swallow the very next utterance in Chrome —
  // that was why the first prompt was silent until you pressed a second time.
  if (synth.speaking || synth.pending) synth.cancel();
  const utterance = new window.SpeechSynthesisUtterance(String(text));
  utterance.lang = "en-US";
  utterance.rate = 0.95;
  const voice = pickEnglishVoice();
  if (voice) utterance.voice = voice;
  synth.speak(utterance);
  // Chrome can leave the queue paused after a cancel(); nudge it.
  if (synth.paused) synth.resume();
};

// Speak the given text aloud. Returns true if speech was dispatched (or queued
// to dispatch once voices load), false if unsupported / it errored.
//
// On the very first call Chrome's voice list is often still empty; speaking
// then can be dropped silently. So if no voices are ready yet we wait for the
// voiceschanged event (with a timeout fallback) before speaking, instead of
// firing into the void.
export const speak = (text) => {
  if (!isSpeechSupported()) return false;
  try {
    const synth = window.speechSynthesis;
    if (getVoices().length > 0) {
      dispatch(text);
      return true;
    }

    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      synth.onvoiceschanged = null;
      dispatch(text);
    };
    synth.onvoiceschanged = fire;
    setTimeout(fire, 250);
    return true;
  } catch (e) {
    return false;
  }
};
